import { RootState } from 'app/store'

import TileLayer from 'ol/layer/Tile'
import { OSM, XYZ } from 'ol/source'
import { createXYZ } from 'ol/tilegrid'
import type { Map } from 'ol'

import { userSelectors } from 'entities/user/model'

import { COMMON_BASE_LAYERS } from '../../config'

import { createXyzLyr } from './create-xyz-lyr'
import { createTileLyr } from './create-tile-lyr'
import { createImgLyr } from './create-img-lyr'

import proj4 from 'proj4'
import { register } from 'ol/proj/proj4.js'

proj4.defs(
	'EPSG:3395',
	'+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs'
)

register(proj4)

type Props = {
	map: Map
	state: RootState
	translate: (val: string) => string
}

export function setBaseLyrs({ map, state, translate }: Props) {
	const schemeLayer = userSelectors.selectSchemeLayer(state)
	const baseLayers = userSelectors.selectBaseLayers(state)

	COMMON_BASE_LAYERS.forEach((layer) => {
		let baseLyr: any

		const { title, id } = layer

		if (id === 'arggis') {
			baseLyr = createArggis('arggis', `${translate(title)}`)
		} else if (id === 'osm') {
			baseLyr = createOsm('osm', `${translate(title)}`)
		} else if (id === 'google1') {
			baseLyr = createGoogle('google1', `${translate(title)}`)
		} else if (id === 'google2') {
			baseLyr = createGoogleSatellite('google2', `${translate(title)}`)
		} else if (id === 'yandex1') {
			baseLyr = createYandex('yandex1', `${translate(title)}`)
		} else if (id === 'yandex2') {
			baseLyr = createYandexSatellite('yandex2', `${translate(title)} `)
		}

		baseLyr.setZIndex(0)
		map.addLayer(baseLyr)

		if (schemeLayer) {
			baseLyr.setVisible(false)
		}
	})

	if (!!baseLayers?.length) {
		baseLayers.forEach((layer) => {
			let lyr: any

			if (+layer.type === 3) {
				lyr = createXyzLyr(layer, true)
			} else if (!!layer.tiled) {
				lyr = createTileLyr(layer, true)
			} else {
				lyr = createImgLyr(layer, true)
			}

			const minzoom =
				!isNaN(+layer.minzoom) && layer.minzoom - 0.5 >= 0
					? layer.minzoom - 0.5
					: layer.minzoom

			const maxzoom = !isNaN(+layer.maxzoom)
				? layer.maxzoom + 0.4999999
				: layer.maxzoom

			lyr.setMinZoom(minzoom)
			lyr.setMaxZoom(maxzoom)
			lyr.setZIndex(0)

			layer.transparent !== undefined &&
				lyr.setOpacity(Number(layer.transparent))

			lyr.setVisible(false)

			map.addLayer(lyr)
		})
	}

	if (schemeLayer) {
		let lyr: any

		if (+schemeLayer.type === 3) {
			lyr = createXyzLyr(schemeLayer, true)
		} else if (!!schemeLayer.tiled) {
			lyr = createTileLyr(schemeLayer, true)
		} else {
			lyr = createImgLyr(schemeLayer, true)
		}

		const minzoom =
			!isNaN(+schemeLayer.minzoom) && schemeLayer.minzoom - 0.5 >= 0
				? schemeLayer.minzoom - 0.5
				: schemeLayer.minzoom

		const maxzoom = !isNaN(+schemeLayer.maxzoom)
			? schemeLayer.maxzoom + 0.4999999
			: schemeLayer.maxzoom

		lyr.setMinZoom(minzoom)
		lyr.setMaxZoom(maxzoom)
		lyr.setZIndex(0)

		schemeLayer.transparent !== undefined &&
			lyr.setOpacity(Number(schemeLayer.transparent))

		lyr.setProperties({ title: translate('scheme') })

		lyr.set(
			'attr',
			'&copy;&nbsp;<a target="_blank" href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
		)

		lyr.setVisible(true)

		map.addLayer(lyr)
	}
}

function createArggis(id: string, title: string) {
	return new TileLayer({
		source: new XYZ({
			url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
			maxZoom: 19,
			interpolate: true,
		}),
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '&copy;&nbsp;Esri',
		},
	})
}

function createGoogleSatellite(id: string, title: string) {
	return new TileLayer({
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '<span>&copy;&nbsp;<a target="_blank" href="http://googlemaps.com/">Google</a></span>',
		},
		source: new XYZ({
			crossOrigin: 'anonymous',
			url: 'https:////mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
			attributions:
				'<span>&copy;&nbsp;<a target="_blank" href="http://googlemaps.com/">Google</a></span>',
		}),
	})
}

function createGoogle(id: string, title: string) {
	return new TileLayer({
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '<span>&copy;&nbsp;<a target="_blank" href="http://googlemaps.com/">Google</a></span>',
		},
		source: new XYZ({
			crossOrigin: 'anonymous',
			url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
			attributions:
				'<span>&copy;&nbsp;<a target="_blank" href="http://googlemaps.com/">Google</a></span>',
		}),
	})
}

function createYandexSatellite(id: string, title: string) {
	return new TileLayer({
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '<span>&copy;&nbsp;<a target="_blank" href="http://maps.yandex.ru">Yandex.Maps</a></span>',
		},
		source: new XYZ({
			crossOrigin: 'anonymous',
			projection: 'EPSG:3395',
			url: 'https://core-sat.maps.yandex.net/tiles?l=sat&v=3.1159.0&x={x}&y={y}&z={z}&lang=ru_RU',
			tileGrid: createXYZ({
				extent: [
					-20037508.342789244, -20037508.342789244, 20037508.342789244,
					20037508.342789244,
				],
			}),
			attributions:
				'<span>&copy;&nbsp;<a target="_blank" href="http://maps.yandex.ru">Yandex.Maps</a></span>',
		}),
	})
}

function createYandex(id: string, title: string) {
	return new TileLayer({
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '<span>&copy;&nbsp;<a target="_blank" href="http://maps.yandex.ru">Yandex.Maps</a></span>',
		},
		source: new XYZ({
			crossOrigin: 'anonymous',
			url: 'https://core-renderer-tiles.maps.yandex.net/tiles?l=map&x={x}&y={y}&z={z}&scale=1&lang=ru_RU',
			projection: 'EPSG:3395',
			tileGrid: createXYZ({
				extent: [
					-20037508.342789244, -20037508.342789244, 20037508.342789244,
					20037508.342789244,
				],
			}),
			tileLoadFunction: function (imageTile, src) {
				try {
					const zoom = imageTile.tileCoord[0]
					const maxNativeZoom = 21

					if (zoom > maxNativeZoom) {
						const scaledSrc = src.replace(
							`z=${zoom}`,
							`z=${String(maxNativeZoom)}`
						)

						//@ts-ignore
						imageTile.getImage().src = scaledSrc
					} else {
						//@ts-ignore
						imageTile.getImage().src = src
					}
				} catch (e) {}
			},
			maxZoom: 21,
			interpolate: true,
			attributions:
				'<span>&copy;&nbsp;<a target="_blank" href="http://maps.yandex.ru">Yandex.Maps</a></span>',
		}),
	})
}

function createOsm(id: string, title: string) {
	return new TileLayer({
		properties: {
			title,
			type: 'base',
			isCommonBase: true,
			idLayer: id,
			attr: '&copy;&nbsp;<a target="_blank" href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		},
		source: new OSM({
			attributions:
				'&copy;&nbsp;<a target="_blank" href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
		}),
	})
}
